import { useState } from "react";
import { BAND_UI } from "@/lib/bands";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Crown, Plus, X } from "lucide-react";

export const TadpolePicker = ({ band, bandData, onPick }) => {
  const ui = BAND_UI[band];
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [frogIdx, setFrogIdx] = useState(null);
  const [guess, setGuess] = useState("");
  const needsGuess = band === "68" || band === "912";

  const add = (title) => {
    const t = title.trim();
    if (!t || items.includes(t)) return;
    setItems([...items, t]);
    setText("");
  };

  const remove = (i) => {
    setItems(items.filter((_, idx) => idx !== i));
    if (frogIdx === i) setFrogIdx(null);
    else if (frogIdx > i) setFrogIdx(frogIdx - 1);
  };

  const submit = () => {
    const tadpoles = items.filter((_, i) => i !== frogIdx);
    onPick(items[frogIdx], "homework", needsGuess && guess ? parseInt(guess, 10) : null, tadpoles);
  };

  return (
    <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-100 shadow-lg space-y-6">
      <div>
        <p className="font-display text-xl sm:text-2xl font-bold text-slate-800">List today's homework, then crown your frog</p>
        <p className="text-sm text-slate-500 mt-1">Add everything due. The hardest one becomes the frog — the rest are tadpoles.</p>
      </div>

      <div className="flex gap-3 items-center">
        <Input
          data-testid="tadpole-add-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add(text)}
          placeholder="Math worksheet, reading log..."
          className="h-12 rounded-xl"
        />
        <Button data-testid="tadpole-add-btn" onClick={() => add(text)} disabled={!text.trim()} className="btn-tactile h-12 rounded-xl bg-sky-500 hover:bg-sky-600 px-5 font-display font-semibold">
          <Plus className="w-5 h-5" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {bandData.frog_ideas.map((idea) => (
          <button
            key={idea}
            data-testid={`tadpole-idea-${idea.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => add(idea)}
            disabled={items.includes(idea)}
            className="px-4 py-2 rounded-full bg-slate-100 hover:bg-sky-100 text-slate-600 text-sm font-semibold transition-colors disabled:opacity-40"
          >
            + {idea}
          </button>
        ))}
      </div>

      {items.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Tap the crown on the HARDEST one — that's your frog</p>
          {items.map((item, i) => (
            <div
              key={item}
              data-testid={`tadpole-item-${i}`}
              className={`flex items-center gap-3 rounded-2xl p-4 border-2 transition-colors ${
                frogIdx === i ? "border-green-500 bg-green-50" : "border-slate-100 bg-slate-50"
              }`}
            >
              <button
                data-testid={`crown-frog-${i}`}
                onClick={() => setFrogIdx(i)}
                className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                  frogIdx === i ? "bg-green-500 text-white" : "bg-white text-slate-300 hover:text-yellow-500 shadow-sm"
                }`}
              >
                <Crown className="w-5 h-5" />
              </button>
              <span className={`flex-1 font-display font-semibold ${frogIdx === i ? "text-green-700" : "text-slate-700"}`}>
                {item} {frogIdx === i && <span className="text-xs font-bold text-green-500 ml-1">THE FROG</span>}
              </span>
              <button onClick={() => remove(i)} className="text-slate-300 hover:text-red-400 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {needsGuess && frogIdx !== null && (
        <div className="flex items-center gap-3 bg-yellow-50 rounded-2xl p-4 border border-yellow-100">
          <p className="text-sm font-semibold text-slate-700">Minutes the frog will take?</p>
          <Input data-testid="tadpole-time-guess-input" type="number" min="1" value={guess} onChange={(e) => setGuess(e.target.value)} placeholder="30" className="w-24 h-11 rounded-xl text-center font-bold" />
        </div>
      )}

      <Button
        data-testid="tadpole-submit-btn"
        onClick={submit}
        disabled={frogIdx === null || items.length === 0}
        className="btn-tactile w-full h-16 rounded-2xl bg-green-500 hover:bg-green-600 font-display font-bold text-xl disabled:opacity-40"
      >
        {frogIdx === null ? "Crown your frog first" : `Frog + ${items.length - 1} tadpole${items.length - 1 === 1 ? "" : "s"} — let's go!`}
      </Button>
      <p className="text-center text-xs text-slate-400">{ui.workingMsg}</p>
    </div>
  );
};
